import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import Button from '@mui/material/Button';

import { coViecDangLam } from 'utils/dangBan';

const CHECK_INTERVAL = 60 * 20 * 1000;
const CURRENT_VERSION = process.env.REACT_APP_VERSION;
// Đã tự tải lại cho phiên bản nào trong phiên này — chống vòng lặp tải lại mãi nếu tải xong mà
// vẫn nhận bản cũ (cache trình duyệt/CDN chưa kịp đổi).
const KHOA_DA_TAI_LAI = 'hdhd-da-tu-tai-lai';

const daTuTaiLaiCho = (phienBan) => {
    try {
        return sessionStorage.getItem(KHOA_DA_TAI_LAI) === phienBan;
    } catch (e) {
        // Không đọc được sessionStorage thì không biết đã tải lại chưa → coi như đã, để an toàn.
        return true;
    }
};

const tuTaiLai = (phienBan) => {
    try {
        sessionStorage.setItem(KHOA_DA_TAI_LAI, phienBan);
    } catch (e) {
        return;
    }
    window.location.reload();
};

/**
 * Báo và đưa người dùng lên phiên bản mới.
 *
 * Điện thoại giữ tab rất lâu: hạnh sinh mở lại tab cũ từ mấy hôm trước là vẫn chạy code cũ
 * (gọi API đã đổi, thiếu tính năng mới). Trước đây chỉ hiện một dòng thông báo nhỏ, phần lớn
 * bỏ qua. Giờ trang TỰ tải lại, nhưng chỉ ở thời điểm an toàn:
 *
 * - Khi chuyển sang trang khác trong app: dữ liệu đang nhập ở trang cũ vốn đã bị bỏ, tải lại
 *   không làm mất thêm gì. KHÔNG tự tải lại khi quay lại tab (có thể đang điền dở báo cáo).
 * - Không tải lại khi có việc đang làm (ghép ảnh, tải tệp, lưu phiếu — xem utils/dangBan).
 * - Mỗi phiên bản chỉ tự tải lại một lần mỗi phiên; còn lại vẫn có nút bấm như cũ.
 */
const AppVersionSnackbar = () => {
    const location = useLocation();
    const [banMoi, datBanMoi] = useState(null);
    const lanDau = useRef(true);

    const kiemTra = useCallback(async () => {
        if (!CURRENT_VERSION) return null;
        try {
            const res = await fetch(`/version.json?ts=${Date.now()}`, { cache: 'no-store' });
            if (!res.ok) return null;
            const latest = (await res.json())?.version;
            if (latest && latest !== CURRENT_VERSION) {
                datBanMoi(latest);
                return latest;
            }
        } catch (e) {
            // Mạng chập chờn: lần sau kiểm tra lại, không làm gì cả.
        }
        return null;
    }, []);

    // Kiểm tra lúc mở trang, định kỳ, và mỗi lần quay lại tab (tab cũ biết ngay có bản mới).
    useEffect(() => {
        kiemTra();
        const hen = setInterval(kiemTra, CHECK_INTERVAL);
        const khiHien = () => {
            if (document.visibilityState === 'visible') kiemTra();
        };
        document.addEventListener('visibilitychange', khiHien);

        return () => {
            clearInterval(hen);
            document.removeEventListener('visibilitychange', khiHien);
        };
    }, [kiemTra]);

    // Chuyển trang = thời điểm an toàn để lên bản mới. Bỏ qua lần render đầu (chưa chuyển trang).
    useEffect(() => {
        if (lanDau.current) {
            lanDau.current = false;
            return;
        }
        if (banMoi && !coViecDangLam() && !daTuTaiLaiCho(banMoi)) tuTaiLai(banMoi);
        // Chỉ chạy khi đổi trang; banMoi mới về giữa chừng thì đợi lần chuyển trang sau.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    return (
        <Snackbar
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left'
            }}
            open={Boolean(banMoi)}
            ContentProps={{
                'aria-describedby': 'message-id'
            }}
            message={<span id="message-id">Hệ thống đã cập nhật phiên bản mới.</span>}
            action={[
                <Button key="undo" color="primary" size="small" onClick={() => window.location.reload()}>
                    Bấm vào đây để cập nhật
                </Button>
            ]}
        />
    );
};

export default AppVersionSnackbar;
